<template>
  <section id="about" class="py-12 md:py-20 bg-background">
    <div class="container mx-auto px-4 md:px-6">
      <div class="max-w-3xl mx-auto mb-8 md:mb-16 text-center">
        <h2 class="text-3xl md:text-4xl font-bold text-navy mb-6">
          {{ t('about.sectionTitle') }}
        </h2>
        <div class="w-24 h-1 bg-leaf mx-auto mb-6 rounded-full"></div>
        <p class="text-lg text-muted-foreground">
          {{ t('about.sectionSubtitle') }}
        </p>
      </div>

      <div class="grid md:grid-cols-2 gap-12 items-center">
        <div>
          <div class="aspect-video rounded-lg overflow-hidden shadow-xl">
            <img :src="aboutImage" alt="Blueberry plantation" width="1200" height="675" loading="lazy" decoding="async" class="w-full h-full object-cover"
              style="object-position: center 60%;" />
          </div>
        </div>

        <div class="space-y-6">
          <h3 class="text-2xl font-bold text-navy">{{ t('about.historyTitle') }}</h3>
          <p class="text-foreground/80">
            {{ t('about.historyP1') }}
          </p>
          <p class="text-foreground/80">
            {{ t('about.historyP2') }}
          </p>

          <div class="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-4">
            <div class="flex flex-col items-center text-center sm:flex-row sm:items-start sm:text-left sm:space-x-3">
              <div class="bg-blueberry-lighter/20 p-3 rounded-full mb-2 sm:mb-0 shrink-0">
                <Package class="text-blueberry w-6 h-6" />
              </div>
              <div>
                <h4 class="font-semibold text-navy">{{ t('about.premiumQuality') }}</h4>
                <p class="text-sm text-muted-foreground">{{ t('about.premiumQualityDesc') }}</p>
              </div>
            </div>

            <div class="flex flex-col items-center text-center sm:flex-row sm:items-start sm:text-left sm:space-x-3">
              <div class="bg-leaf-light/20 p-3 rounded-full mb-2 sm:mb-0 shrink-0">
                <Leaf class="text-leaf w-6 h-6" />
              </div>
              <div>
                <h4 class="font-semibold text-navy">{{ t('about.organic') }}</h4>
                <p class="text-sm text-muted-foreground">{{ t('about.organicDesc') }}</p>
              </div>
            </div>

            <div class="flex flex-col items-center text-center sm:flex-row sm:items-start sm:text-left sm:space-x-3">
              <div class="bg-violet/15 p-3 rounded-full mb-2 sm:mb-0 shrink-0">
                <Award class="text-violet w-6 h-6" />
              </div>
              <div>
                <h4 class="font-semibold text-navy">{{ t('about.bestTaste') }}</h4>
                <p class="text-sm text-muted-foreground">{{ t('about.bestTasteDesc') }}</p>
              </div>
            </div>

            <div class="flex flex-col items-center text-center sm:flex-row sm:items-start sm:text-left sm:space-x-3">
              <div class="bg-navy/15 p-3 rounded-full mb-2 sm:mb-0 shrink-0">
                <ThumbsUp class="text-navy w-6 h-6" />
              </div>
              <div>
                <h4 class="font-semibold text-navy">{{ t('about.customerFavourite') }}</h4>
                <p class="text-sm text-muted-foreground">{{ t('about.customerFavouriteDesc', { rating: displayRating }) }}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
import aboutImage from "@/assets/onas.avif";
import { Package, Leaf, Award, ThumbsUp } from "lucide-vue-next";
import { computed } from "vue";
import { useI18n } from "vue-i18n";
import { useReviews } from "@acme/api";

const FALLBACK_RATING = "4.8";

const { t, locale } = useI18n();
const reviewsResult = useReviews().getReviews(locale.value);

const displayRating = computed(() => {
  const result = reviewsResult.value;
  if (result.success && result.content.rating !== null) {
    return result.content.rating.toFixed(1);
  }
  return FALLBACK_RATING;
});
</script>
